import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { services } from '../data/services';

export default function Header() {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 10) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  const menuItems = [
    { label: 'Forside', path: '/' },
    { label: 'Jeg tilbyder', path: '/jeg-tilbyder' },
    { label: 'Kunderne siger', path: '/kunderne-siger' },
    { label: 'Galleri', path: '/galleri' },
    { label: 'Om os', path: '/om-os' },
    { label: 'Kontakt', path: '/kontakt' },
  ];

  const isActive = (path) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <header
      className={`sticky top-0 z-50 w-full transition-all duration-300 bg-black border-b border-slate-800/60 ${
        isScrolled ? 'py-2 shadow-lg shadow-black/20' : 'py-4'
      }`}
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex py-2 md:py-3 items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group">
            <img
              src="https://assets.auxodev.dk/cdn-cgi/image/height=240,format=auto/assets/cf131c5c-877c-4f32-8100-a69e4d795732/k-logo.png"
              alt="K. Pheiffer Construction Logo"
              className="h-20 md:h-24 w-auto object-contain transition-transform duration-300 group-hover:scale-105"
            />
          </Link>

          {/* Desktop Navigation (>= 1200px) */}
          <nav className="hidden min-[1200px]:flex items-center gap-8">
            {menuItems.map((item) => {
              if (item.path === '/jeg-tilbyder') {
                return (
                  <div key={item.path} className="relative group py-2">
                    <Link
                      to={item.path}
                      className={`text-sm font-medium tracking-wide transition-colors duration-200 relative py-1 flex items-center gap-1.5 ${
                        isActive(item.path)
                          ? 'text-white font-semibold'
                          : 'text-slate-300 hover:text-white'
                      }`}
                    >
                      {item.label}
                      <i className="fa-regular fa-chevron-down text-[10px] transition-transform duration-200 group-hover:rotate-180" />
                      {isActive(item.path) && (
                        <span className="absolute bottom-0 left-0 h-[2px] w-full bg-emerald-500 rounded-full" />
                      )}
                    </Link>

                    {/* Dropdown Menu */}
                    <div className="absolute left-0 top-full mt-1 w-64 rounded-lg bg-slate-950 border border-slate-800 shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 py-2">
                      {services.map((service) => (
                        <Link
                          key={service.id}
                          to={`/jeg-tilbyder/${service.id}`}
                          className="block px-4 py-2.5 text-sm text-slate-300 hover:text-white hover:bg-slate-900 transition-colors"
                        >
                          <span className="font-medium">{service.title}</span>
                        </Link>
                      ))}
                    </div>
                  </div>
                );
              }

              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`text-sm font-medium tracking-wide transition-colors duration-200 relative py-1 ${
                    isActive(item.path)
                      ? 'text-white font-semibold'
                      : 'text-slate-300 hover:text-white'
                  }`}
                >
                  {item.label}
                  {isActive(item.path) && (
                    <span className="absolute bottom-0 left-0 h-[2px] w-full bg-emerald-500 rounded-full" />
                  )}
                </Link>
              );
            })}
          </nav>

          {/* Desktop CTA (>= 1200px) */}
          <div className="hidden min-[1200px]:flex items-center">
            <Link
              to="/kontakt"
              className="inline-flex items-center justify-center rounded bg-emerald-600 hover:bg-emerald-500 text-white px-5 py-2.5 text-sm font-semibold tracking-wide transition-all duration-200 hover:shadow-lg hover:shadow-emerald-900/20 active:scale-95"
            >
              Få et tilbud
            </Link>
          </div>

          {/* Mobile Menu Button (< 1200px) */}
          <div className="flex min-[1200px]:hidden">
            <button
              onClick={() => setIsOpen(!isOpen)}
              type="button"
              className="inline-flex items-center justify-center rounded-md p-2.5 text-slate-400 hover:bg-slate-900 hover:text-white focus:outline-none"
              aria-controls="mobile-menu"
              aria-expanded={isOpen}
            >
              <span className="sr-only">Åbn menu</span>
              {isOpen ? (
                <i className="fa-regular fa-xmark text-2xl w-6 h-6 flex items-center justify-center" />
              ) : (
                <i className="fa-regular fa-bars text-2xl w-6 h-6 flex items-center justify-center" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu (< 1200px) */}
      <div
        className={`min-[1200px]:hidden transition-all duration-300 ease-in-out ${
          isOpen ? 'max-h-[calc(100vh-6rem)] overflow-y-auto opacity-100 border-t border-slate-800/60 mt-3' : 'max-h-0 overflow-hidden opacity-0'
        }`}
        id="mobile-menu"
      >
        <div className="space-y-1 px-4 py-4 bg-black">
          {menuItems.map((item) => (
            <div key={item.path}>
              <Link
                to={item.path}
                className={`block rounded-md px-3 py-3 text-base font-medium transition-colors ${
                  isActive(item.path)
                    ? 'bg-slate-900 text-white font-semibold'
                    : 'text-slate-300 hover:bg-slate-900/50 hover:text-white'
                }`}
              >
                {item.label}
              </Link>
              {item.path === '/jeg-tilbyder' && (
                <div className="pl-6 mt-1 space-y-1 border-l border-slate-800 ml-3">
                  {services.map((service) => (
                    <Link
                      key={service.id}
                      to={`/jeg-tilbyder/${service.id}`}
                      className="block rounded-md px-3 py-2 text-sm text-slate-400 hover:bg-slate-900/30 hover:text-white transition-colors"
                    >
                      {service.title}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          ))}
          <div className="pt-4 pb-2 px-3">
            <Link
              to="/kontakt"
              className="flex w-full items-center justify-center rounded bg-emerald-600 hover:bg-emerald-500 text-white py-3 text-base font-semibold tracking-wide transition-colors"
            >
              Få et tilbud
            </Link>
          </div>
        </div>
      </div>
    </header>
  );
}
